import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Share2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { MediaThumb, fmtWhen } from "@/lib/social";
import { PostTable } from "@/components/social/PostTable";
import { PostReviewModal } from "@/components/social/PostReviewModal";
import { InstagramGrid, ViewSwitch, igAccounts } from "@/components/social/InstagramGrid";

function GroupList({ slug }) {
  const nav = useNavigate();
  const [groups, setGroups] = useState(null);
  useEffect(() => { api.get("/social/groups").then(({ data }) => setGroups(data.groups)).catch(() => setGroups([])); }, []);
  return (
    <main className="px-0 sm:px-6 md:px-10 py-0 sm:py-10 max-w-6xl mx-auto" data-testid="client-social-page">
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Social posts</h1>
      <p className="text-sm text-[#595D62] mt-1.5 mb-8">Review and approve the posts your team has prepared.</p>
      {groups === null ? <Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc] mx-auto mt-16" /> : !groups.length ? <p className="text-sm text-[#9FA1A4] py-16 text-center">Nothing to review yet.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <button key={g.id} data-testid={`client-social-group-${g.id}`} onClick={() => nav(`/${slug}/social/${g.id}`)} className="text-left rounded-2xl border border-[#EEEFEF] bg-white overflow-hidden hover:border-[#d9ccff] transition-colors">
              <div className="h-36 bg-[#F6F6FD] relative">{g.thumb ? <MediaThumb m={g.thumb} className="h-full w-full" /> : <Share2 className="h-8 w-8 text-[#d9ccff] absolute inset-0 m-auto" />}
                {g.pending > 0 ? <span className="absolute top-3 left-3 rounded-full bg-amber-50 text-amber-700 px-2.5 py-0.5 text-[11px] font-medium">{g.pending} to review</span>
                  : <span className="absolute top-3 left-3 rounded-full bg-[#E6F7EE] text-[#1f9d55] px-2.5 py-0.5 text-[11px] font-medium">Reviewed</span>}</div>
              <div className="p-4"><p className="font-medium text-[#06060C] truncate">{g.name}</p><p className="text-xs text-[#595D62] mt-1">{g.total} post{g.total === 1 ? "" : "s"} · {g.approved} approved · sent {fmtWhen(g.sent_to_client?.at)}</p></div>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}

function GroupReview({ slug, gid }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [view, setView] = useState("table");
  const load = useCallback(() => api.get(`/social/groups/${gid}`).then(({ data }) => setD(data)).catch(() => setD(false)), [gid]);
  useEffect(() => { load(); }, [load]);
  if (d === null) return <Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc] mx-auto mt-24" />;
  if (d === false) return <p className="py-24 text-center text-sm text-[#9FA1A4]">These posts couldn't be found.</p>;
  const pending = d.posts.filter((p) => p.status === "with_client").length;
  const post = open && d.posts.find((p) => p.id === open.id);
  const quick = async (p) => {
    try { await api.post(`/social/posts/${p.id}/decision`, { decision: "approve" }); toast.success("Post approved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <main className="px-0 sm:px-6 md:px-10 py-0 sm:py-8 max-w-7xl mx-auto space-y-5" data-testid="client-social-group">
      <button data-testid="client-social-back" onClick={() => nav(`/${slug}/social`)} className="text-sm text-[#723FFF] flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> All social posts</button>
      <div className="flex items-end gap-4 flex-wrap">
       <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] break-words">{d.group.name}</h1>
        <p className="text-sm text-[#595D62] mt-1.5" data-testid="client-social-summary">{pending ? `${pending} post${pending === 1 ? "" : "s"} waiting for your review. Tap a post to preview it, then approve or reject it.` : <span className="inline-flex items-center gap-1.5 text-[#1f9d55]"><CheckCircle2 className="h-4 w-4" /> All posts reviewed. Thank you!</span>}</p>
       </div>
       <ViewSwitch view={view} onChange={setView} show={igAccounts(d.posts, d.accounts).length > 0} />
      </div>
      {view === "grid" && igAccounts(d.posts, d.accounts).length ? <InstagramGrid posts={d.posts} accounts={d.accounts} ws={d.workspace} onOpen={(p) => setOpen({ id: p.id })} /> : <PostTable posts={d.posts} accounts={d.accounts} ws={d.workspace} client onOpen={(p, mode) => setOpen({ id: p.id, mode })} onQuick={quick} />}
      {post && <PostReviewModal key={post.id + post.status} post={post} accounts={d.accounts} ws={d.workspace} rejectMode={open.mode === "reject"} onClose={() => setOpen(null)} onChanged={(decided) => { load(); if (decided) setOpen(null); }} />}
    </main>
  );
}

export default function ClientSocial() {
  const { slug, groupId } = useParams();
  return groupId ? <GroupReview slug={slug} gid={groupId} /> : <GroupList slug={slug} />;
}
